import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Users, CheckCircle, XCircle, Clock, Search,
    ChevronDown, UserCheck, UserX, Shield, Pencil, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function UserManagement({ user }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [squadInput, setSquadInput] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('');
    const [nameInput, setNameInput] = useState('');
    const [roleInput, setRoleInput] = useState('');
    const [statusInput, setStatusInput] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    const handleEditClick = (user) => {
        setSelectedUser(user);
        setIsEditing(true);
    };

    const handleUpdate = async () => {
        if (!selectedUser) return;
        try {
            const updatePayload = {
                squad: squadInput || undefined,
                company_id: selectedCompany || undefined
            };
            if (nameInput && nameInput !== selectedUser.name) {
                updatePayload.name = nameInput;
            }
            if (user?.role === 'super_admin') {
                if (roleInput) updatePayload.role = roleInput;
                if (statusInput) updatePayload.status = statusInput;
            }
            await axios.put(`${API}/admin/users/${selectedUser.user_id}`, updatePayload, { withCredentials: true });
            toast.success('Usuário atualizado com sucesso!');
            setSelectedUser(null);
            setIsEditing(false);
            setSquadInput('');
            setNameInput('');
            setRoleInput('');
            setStatusInput('');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
        }
    };

    const handleDelete = async (userId, userName) => {
        if (!window.confirm(`Tem certeza que deseja EXCLUIR o usuário "${userName}"? Esta ação não pode ser desfeita.`)) return;
        try {
            await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
            toast.success(`Usuário "${userName}" excluído com sucesso!`);
            setSelectedUser(null);
            setIsEditing(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
        }
    };

    useEffect(() => {
        if (selectedUser) {
            setSelectedCompany(selectedUser.company_id || '');
            setSquadInput(selectedUser.squad || '');
            setNameInput(selectedUser.name || '');
            setRoleInput(selectedUser.role || 'student');
            setStatusInput(selectedUser.status || 'approved');
        }
    }, [selectedUser]);

    useEffect(() => {
        fetchUsers();
        fetchCompanies();
    }, [filterStatus, filterCompany]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            let url = `${API}/admin/users`;
            const params = [];
            if (filterStatus) params.push(`status=${filterStatus}`);
            if (filterCompany) params.push(`company_id=${filterCompany}`);
            if (params.length) url += `?${params.join('&')}`;

            const response = await axios.get(url, { withCredentials: true });
            setUsers(response.data);
        } catch (error) {
            toast.error('Erro ao carregar usuários');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await axios.get(`${API}/admin/companies`, { withCredentials: true });
            setCompanies(response.data);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const handleApprove = async (userId) => {
        try {
            await axios.put(`${API}/admin/users/${userId}/approve`, {
                squad: squadInput || undefined,
                company_id: selectedCompany || undefined
            }, { withCredentials: true });
            toast.success('Usuário aprovado!');
            setSelectedUser(null);
            setSquadInput('');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao aprovar usuário');
        }
    };

    const handleReject = async (userId) => {
        try {
            await axios.put(`${API}/admin/users/${userId}/reject`, {}, { withCredentials: true });
            toast.success('Usuário rejeitado');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao rejeitar usuário');
        }
    };

    const handlePromote = async (userId, role) => {
        try {
            await axios.put(`${API}/admin/users/${userId}`, { role }, { withCredentials: true });
            toast.success('Cargo atualizado!');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar cargo');
        }
    };

    const filteredUsers = users.filter(u => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term);
    });

    const pendingCount = users.filter(u => u.status === 'pending').length;
    const approvedCount = users.filter(u => u.status === 'approved').length;

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
            approved: 'bg-green-500/10 text-green-600 dark:text-green-400',
            rejected: 'bg-red-500/10 text-red-600 dark:text-red-400'
        };
        const labels = { pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado' };
        return (
            <span className={`px-2 py-1 text-xs uppercase tracking-wide rounded-sm ${styles[status] || ''}`}>
                {labels[status] || status}
            </span>
        );
    };

    const getRoleBadge = (role) => {
        const styles = {
            super_admin: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
            admin: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
            student: 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
        };
        const labels = { super_admin: 'Super Admin', admin: 'Admin', student: 'Estudante' };
        return (
            <span className={`px-2 py-1 text-xs uppercase tracking-wide rounded-sm ${styles[role] || ''}`}>
                {labels[role] || role}
            </span>
        );
    };

    return (
        <div>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="p-4 rounded-none border border-border">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-500/10 rounded-none">
                            <Clock className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{pendingCount}</p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Pendentes</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4 rounded-none border border-border">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-none">
                            <UserCheck className="h-5 w-5 text-green-500" strokeWidth={1.5} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{approvedCount}</p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Aprovados</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4 rounded-none border border-border">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-none">
                            <Users className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{users.length}</p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por nome ou email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 rounded-none"
                    />
                </div>
                <select
                    className="px-3 py-2 border border-border bg-background text-sm"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                >
                    <option value="">Todos os status</option>
                    <option value="pending">Pendentes</option>
                    <option value="approved">Aprovados</option>
                    <option value="rejected">Rejeitados</option>
                </select>
                {user?.role === 'super_admin' && (
                    <select
                        className="px-3 py-2 border border-border bg-background text-sm"
                        value={filterCompany}
                        onChange={(e) => setFilterCompany(e.target.value)}
                    >
                        <option value="">Todas as empresas</option>
                        {companies.map(c => (
                            <option key={c.company_id} value={c.company_id}>{c.name}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* Users Table */}
            <Card className="rounded-none border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr>
                                <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Usuário</th>
                                <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Empresa</th>
                                <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Squad</th>
                                <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Status</th>
                                <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Cargo</th>
                                <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filteredUsers.map((u) => (
                                <tr key={u.user_id} className="hover:bg-muted/30">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            {u.picture && (
                                                <img src={u.picture} alt="" className="h-8 w-8 rounded-full" />
                                            )}
                                            <div>
                                                <p className="font-medium">{u.name}</p>
                                                <p className="text-xs text-muted-foreground">{u.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-muted-foreground">{u.company_name || '-'}</td>
                                    <td className="px-6 py-4 text-muted-foreground">{u.squad || '-'}</td>
                                    <td className="px-6 py-4">{getStatusBadge(u.status || 'approved')}</td>
                                    <td className="px-6 py-4">{getRoleBadge(u.role)}</td>

                                    <td className="px-6 py-4">
                                        <div className="flex gap-1">
                                            {(u.status === 'pending') && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => { setSelectedUser(u); setIsEditing(false); }}
                                                        className="rounded-none text-green-600 hover:text-green-500"
                                                        title="Aprovar"
                                                    >
                                                        <CheckCircle className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleReject(u.user_id)}
                                                        className="rounded-none text-red-600 hover:text-red-500"
                                                        title="Rejeitar"
                                                    >
                                                        <XCircle className="h-4 w-4" />
                                                    </Button>
                                                </>
                                            )}
                                            {/* Edit button for ALL users */}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleEditClick(u)}
                                                className="rounded-none text-blue-600 hover:text-blue-500"
                                                title="Editar"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            {user?.role === 'super_admin' && u.role !== 'super_admin' && u.status === 'approved' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handlePromote(u.user_id, u.role === 'admin' ? 'student' : 'admin')}
                                                    className="rounded-none"
                                                    title={u.role === 'admin' ? 'Remover Admin' : 'Tornar Admin'}
                                                >
                                                    <Shield className="h-4 w-4" />
                                                </Button>
                                            )}
                                            {/* Delete button */}
                                            {user?.role === 'super_admin' && u.role !== 'super_admin' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(u.user_id, u.name)}
                                                    className="rounded-none text-red-600 hover:text-red-500 hover:bg-red-600/10"
                                                    title="Excluir usuário"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filteredUsers.length === 0 && (
                    <div className="p-12 text-center text-muted-foreground">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Nenhum usuário encontrado</p>
                    </div>
                )}
            </Card>

            {/* Approval/Edit Modal */}
            {selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedUser(null); setIsEditing(false); }}>
                    <Card className="max-w-md w-full p-6 rounded-none" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {isEditing ? 'Editar Usuário' : 'Aprovar Usuário'}
                        </h3>
                        <div className="space-y-4 mb-6">
                            <div>
                                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                            </div>

                            {/* Name field - editable */}
                            <div>
                                <label className="text-sm font-medium mb-2 block">Nome</label>
                                <Input
                                    value={nameInput}
                                    onChange={(e) => setNameInput(e.target.value)}
                                    placeholder="Nome do usuário"
                                    className="rounded-none"
                                />
                            </div>

                            {user?.role === 'super_admin' && (
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Empresa</label>
                                    <select
                                        className="w-full px-3 py-2 border border-border bg-background text-sm rounded-none"
                                        value={selectedCompany}
                                        onChange={(e) => setSelectedCompany(e.target.value)}
                                    >
                                        <option value="">Selecione uma empresa...</option>
                                        {companies.map(c => (
                                            <option key={c.company_id} value={c.company_id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="text-sm font-medium mb-2 block">Squad (opcional)</label>
                                <Input
                                    value={squadInput}
                                    onChange={(e) => setSquadInput(e.target.value)}
                                    placeholder="Ex: Turma A, Grupo 1..."
                                    className="rounded-none"
                                />
                            </div>

                            {/* Role field - super_admin only */}
                            {user?.role === 'super_admin' && isEditing && (
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Cargo</label>
                                    <select
                                        className="w-full px-3 py-2 border border-border bg-background text-sm rounded-none"
                                        value={roleInput}
                                        onChange={(e) => setRoleInput(e.target.value)}
                                    >
                                        <option value="student">Estudante</option>
                                        <option value="admin">Admin</option>
                                        <option value="super_admin">Super Admin</option>
                                    </select>
                                </div>
                            )}

                            {/* Status field - super_admin only */}
                            {user?.role === 'super_admin' && isEditing && (
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Status</label>
                                    <select
                                        className="w-full px-3 py-2 border border-border bg-background text-sm rounded-none"
                                        value={statusInput}
                                        onChange={(e) => setStatusInput(e.target.value)}
                                    >
                                        <option value="pending">Pendente</option>
                                        <option value="approved">Aprovado</option>
                                        <option value="rejected">Rejeitado</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => { setSelectedUser(null); setIsEditing(false); }}
                                className="flex-1 rounded-none"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={isEditing ? handleUpdate : () => handleApprove(selectedUser.user_id)}
                                className={`flex-1 rounded-none text-white ${isEditing ? 'bg-blue-600 hover:bg-blue-500' : 'bg-green-600 hover:bg-green-500'}`}
                            >
                                {isEditing ? <Pencil className="h-4 w-4 mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                                {isEditing ? 'Salvar' : 'Aprovar'}
                            </Button>
                        </div>
                        {/* Delete button in modal - super_admin only */}
                        {user?.role === 'super_admin' && selectedUser.role !== 'super_admin' && isEditing && (
                            <Button
                                variant="outline"
                                onClick={() => handleDelete(selectedUser.user_id, selectedUser.name)}
                                className="w-full mt-3 rounded-none text-red-600 border-red-600/30 hover:bg-red-600/10 hover:text-red-500"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir Usuário
                            </Button>
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
}
